import { useCallback, useEffect, useState } from 'react';
import {
	fireOperationalAnalytics,
	fireTrackAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { RequestTypeIssueSecurityStatus } from '@atlassian/jira-servicedesk-ticket-types-store/src/common/types.tsx';
import { useDeleteIssueSecurity } from '../../services/delete-issue-security/index.tsx';
import { useLoadIssueSecurity } from '../../services/load-issue-security/index.tsx';
import { useSaveIssueSecurity } from '../../services/save-issue-security/index.tsx';
import type { RequestTypeIssueSecurityState, SecurityLevelOption } from './types.tsx';
import {
	isDefaultNotAllowed,
	isMisconfiguredMissingLevel,
	isMissingValueForRequiredField,
} from './utils.tsx';

export const useIssueSecurity = (projectId: number, requestTypeExternalId: string) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		data: issueSecurityConfigData,
		loading: issueSecurityConfigLoading,
		error: issueSecurityConfigError,
		fetch: issueSecurityConfigFetch,
	} = useLoadIssueSecurity(projectId, requestTypeExternalId);

	const [uiSecurityLevelOption, setSecurityLevelOption] = useState<SecurityLevelOption>('DEFAULT');
	const [uiSelectedSecurityLevelId, setSelectedSecurityLevelId] = useState<string>();
	const [uiShowError, setShowError] = useState(false);

	const { loading: saveLoading, fetch: saveFetch } = useSaveIssueSecurity(
		projectId,
		requestTypeExternalId,
	);
	const { loading: deleteLoading, fetch: deleteFetch } = useDeleteIssueSecurity(
		projectId,
		requestTypeExternalId,
	);

	const showError = () => setShowError(true);
	const makeSelection = useCallback(
		(securityLevelOption: SecurityLevelOption, selectedSecurityLevelId?: string) => {
			setSecurityLevelOption(securityLevelOption);
			setSelectedSecurityLevelId(
				securityLevelOption === 'SELECT' ? selectedSecurityLevelId : undefined,
			);
			setShowError(false);
		},
		[],
	);

	useEffect(() => {
		if (issueSecurityConfigData) {
			const securityLevelOption =
				issueSecurityConfigData.selectedSecurityLevelId ||
				isDefaultNotAllowed(issueSecurityConfigData)
					? 'SELECT'
					: 'DEFAULT';
			makeSelection(securityLevelOption, issueSecurityConfigData.selectedSecurityLevelId);

			const isError =
				isMisconfiguredMissingLevel(issueSecurityConfigData) ||
				isMissingValueForRequiredField(issueSecurityConfigData);
			setShowError(isError);
		}
	}, [issueSecurityConfigData, makeSelection]);

	const fireAnalytics = (result: 'succeed' | 'fail') => {
		const attributes = {
			hasDefaultSecurityLevel: !!issueSecurityState?.defaultSecurityLevelId,
			selectedSecurityLevelId: issueSecurityState?.selectedSecurityLevelId,
			defaultSecurityLevelId: issueSecurityState?.defaultSecurityLevelId,
			issueSecurityLevelsCount: issueSecurityState?.securityLevels.length,
			issueSecurityUpdateType: uiSelectedSecurityLevelId ? 'save' : 'delete',
		};
		return result === 'succeed'
			? fireTrackAnalytics(createAnalyticsEvent({}), 'requestTypeIssueSecurity updated', attributes)
			: fireOperationalAnalytics(
					createAnalyticsEvent({}),
					'requestTypeIssueSecurity updateFailed',
					attributes,
				);
	};

	/**
	 * If response was successful, we expect null to be returned as the body is empty.
	 * Otherwise, we expect undefined or something else in the error case, which we convert to an exception.
	 */
	const applyIssueSecurity = async (): Promise<RequestTypeIssueSecurityStatus> => {
		if (!issueSecurityConfigData) {
			fireAnalytics('fail');
			throw Error('Issue security does not exist.');
		}
		if (uiSelectedSecurityLevelId === issueSecurityConfigData?.selectedSecurityLevelId) {
			return issueSecurityConfigData?.issueSecurityStatus;
		}
		if (uiSelectedSecurityLevelId === undefined) {
			const result = await deleteFetch();
			if (result !== null) {
				fireAnalytics('fail');
				throw Error('Failed to delete issue security');
			}
			fireAnalytics('succeed');
			return issueSecurityConfigData?.defaultSecurityLevelId ? 'ACTIVE' : 'INACTIVE';
		}

		const result = await saveFetch({
			issueSecurityLevelId: uiSelectedSecurityLevelId,
		});
		if (result !== null) {
			fireAnalytics('fail');
			throw Error('Failed to save issue security');
		}
		fireAnalytics('succeed');
		return 'ACTIVE';
	};

	const issueSecurityState: RequestTypeIssueSecurityState | undefined = issueSecurityConfigData && {
		selectedSecurityLevelId: uiSelectedSecurityLevelId,
		defaultSecurityLevelId: issueSecurityConfigData.defaultSecurityLevelId,
		securityLevels: issueSecurityConfigData.securityLevels,
		issueSecurityStatus: issueSecurityConfigData.issueSecurityStatus,
		isRequired: issueSecurityConfigData.isRequired,
		securityLevelOption: uiSecurityLevelOption,
		showError: uiShowError,
	};
	return {
		issueSecurityState,
		issueSecurityConfigLoading,
		issueSecurityConfigError,
		issueSecurityConfigFetch,
		applyIssueSecurity,
		applyIssueSecurityLoading: saveLoading || deleteLoading,
		makeSelection,
		showError,
	};
};
